// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import baseTheme from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import color from "@synd/XDL/Utilities/color";
import { xScreen } from "@synd/XDL/Utilities";
import styled, { css, CSSProperties } from "styled-components";

export const layoutContainer = css`
    @media print {
        max-width: 100% !important;
        margin: 0 !important;
    }

    ${({ theme }: any) => {
        const { maxWidths }: { maxWidths: number[] } = theme.breakpoints || baseTheme.breakpoints;
        const { paddingValues = [16] }: { paddingValues: number[] } = theme.breakpoints;

        const containerStyles = maxWidths.map(
            (maxWidth, index) => css`
                &:not(.full-width) {
                    max-width: ${maxWidth}px;
                    padding-left: ${paddingValues[index] || paddingValues[0]}px;
                    padding-right: ${paddingValues[index] || paddingValues[0]}px;
                    margin-right: auto;
                    margin-left: auto;
                }
                &.full-width {
                    max-width: 100%;
                    margin-right: 0;
                    margin-left: 0;
                    padding-left: 0;
                    padding-right: 0;
                }
            `,
        );
        return css`
            ${breakpointMediaQueries(theme, containerStyles)}
        `;
    }}
`;

/**
 * Hide element in desktop and print mode
 */
export const hideElement = css`
    &&&& {
        display: none;
        @media print {
            display: none;
        }
    }
`;

/**
 * Hide element in print mode
 */
export const hideElementOnPrint = css`
    &&&& {
        @media print {
            display: none;
        }
    }
`;

/**
 * Avoid break element in print mode
 */
export const avoidBreakInsidePrintElement = css`
    @media print {
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
    }
`;

/**
 * Display flex on print
 */
export const displayFlexOnPrint = css`
    &&&& {
        @media print {
            display: flex;
        }
    }
`;

type DividerProps = {
    direction?: "vertical" | "horizontal";
    margin?: CSSProperties["margin"];
    color?: string;
    // "sm" | "md" | "lg" | "xl" | "number"
    flipVerticalOn?: string | number;
};

export const Divider = styled.div<DividerProps>`
    background-color: ${({ color: dividerColor }) => dividerColor || color("common.border")};
    align-self: stretch;
    ${({ direction, margin, flipVerticalOn }: any) =>
        direction === "vertical"
            ? css`
                  min-height: 100%;
                  height: auto;
                  width: 1px;
                  min-width: 1px;
                  margin: ${margin || `0 12px`};
                  ${flipVerticalOn &&
                  css`
                      ${xScreen("lte", flipVerticalOn)} {
                          width: 100%;
                          height: 1px;
                      }
                  `}
              `
            : `
                width: 100%; 
                height: 1px;
                margin: ${margin || `12px 0`};
        `}
`;

export const CenteringWrapper = styled.div<InjectableCss & { height?: number }>`
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${({ height }) => (height ? `${height}px` : "100px")};
    width: 100%;
    padding: 12px;
    ${({ css }) => css}
`;
